import { useEffect, useState } from 'react';
import { DocCompareChunkFinderChunk } from '~/api/def/endpoints/docCompare/docCompareChunkFinderEndpoint';
import { FundGetData } from '~/api/def/endpoints/fundEndpoint';
import { PortfolioCompanyResponse } from '~/api/def/endpoints/portfolioCompanyEndpoint';
import { SearchResults } from '~/components/ui/globalSearch/SearchResults';

// Extend the DocCompareChunkFinderChunk interface to include the _originalFund and _originalPortfolio properties
interface ExtendedDocCompareChunkFinderChunk
  extends DocCompareChunkFinderChunk {
  _originalFund?: FundGetData;
  _originalPortfolio?: PortfolioCompanyResponse;
}

export type SearchResultsProps = {
  results: ExtendedDocCompareChunkFinderChunk[] | null;
  onResultClick?: (result: ExtendedDocCompareChunkFinderChunk) => void;
  onClose?: () => void;
};
export type SearchResultGroup = {
  results: ExtendedDocCompareChunkFinderChunk[];
  name?: string;
};

// Define the order in which groups should be rendered
const groupRenderOrder = [
  'Funds',
  'Portfolios',
  'Documents',
  'Clauses',
  'Definitions',
  'Sections',
];

const EMPTY_DOC_TYPE = 'Unknown';
const MAX_RESULTS_PER_GROUP = 3; // Maximum number of results to show per group in the All tab

/**
 * ResultGroups component groups search results by document type "document" | "fund" | "company" | "widget" | "obligation" | "Unknown"
 * @param results
 * @param onResultClick Optional callback when a result is clicked
 * @param onClose Optional callback to close the search box
 */
export const ResultGroups = ({
  results,
  onResultClick,
  onClose,
}: SearchResultsProps) => {
  const [resultGroups, setResultGroups] = useState<SearchResultGroup[]>([]);
  const [foundGroups, setFoundGroups] = useState<string[]>([]);
  const [isAllCategory, setIsAllCategory] = useState<boolean>(false);

  useEffect(() => {
    console.log('ResultGroups received results:', results);

    // Log all result types to help diagnose issues
    if (results && results.length > 0) {
      console.log(
        'All result types:',
        results.map((r) => r.type),
      );

      // Check specifically for document results
      const documentResults = results.filter(
        (r) => r.type === 'document' || r.type === 'Documents',
      );
      console.log(
        'Document results found:',
        documentResults.length,
        documentResults,
      );
    }

    if (!results) {
      setResultGroups([]);
      setFoundGroups([]);
      return;
    }

    // Check if we're in the All category by looking at the variety of result types
    const uniqueTypes = new Set(
      results.map((result) => result.type || EMPTY_DOC_TYPE),
    );
    console.log('Unique result types:', Array.from(uniqueTypes));
    setIsAllCategory(uniqueTypes.size > 1);

    const groups: SearchResultGroup[] = [];
    const found: string[] = [];

    // Group results by type
    results.forEach((result) => {
      const resultType = result.type ?? EMPTY_DOC_TYPE;
      console.log('Processing result of type:', resultType, 'Result:', result);

      // Check if this is a portfolio result
      if (result._originalPortfolio) {
        console.log('Found portfolio result:', result);
      }

      // Check if the result type is in the groupRenderOrder
      if (!groupRenderOrder.includes(resultType)) {
        console.log(
          'Warning: Result type not in groupRenderOrder:',
          resultType,
        );
      }

      const group = groups.find(
        (group: SearchResultGroup) => group.name === resultType,
      );

      if (group) {
        group.results.push(result);
      } else {
        groups.push({
          name: resultType,
          results: [result],
        });
        found.push(resultType);
      }
    });

    console.log('Result groups:', groups);
    console.log('Found groups:', found);
    console.log('Group render order:', groupRenderOrder);

    setResultGroups(groups);
    setFoundGroups(found);
  }, [results]);

  if (!results || results.length === 0) {
    return (
      <div
        className="text-center mt-3"
        style={{ color: 'var(--bq-text-secondary)' }}
      >
        No results found
      </div>
    );
  }

  return (
    <div className="w-full flex flex-column gap-4">
      {groupRenderOrder.map((groupName) => {
        console.log(
          'Checking group:',
          groupName,
          'Is in found groups:',
          foundGroups.includes(groupName),
        );

        if (foundGroups.includes(groupName)) {
          const groupItem = resultGroups.find(
            (group) => group.name === groupName,
          );

          console.log('Found group item:', groupItem);

          if (!groupItem) return null;

          // Limit results to MAX_RESULTS_PER_GROUP if in All category
          const limitedResults = isAllCategory
            ? groupItem.results.slice(0, MAX_RESULTS_PER_GROUP)
            : groupItem.results;

          console.log(
            'Limited results for group',
            groupName,
            ':',
            limitedResults,
          );

          return (
            <div key={groupItem.name} className="w-full">
              <div className="flex justify-content-between align-items-center mb-3">
                <h3
                  style={{
                    textTransform: 'uppercase',
                    margin: '0',
                    color: 'var(--bq-color-info, #3B82F6)',
                  }}
                >
                  {groupItem.name}
                </h3>
                {isAllCategory &&
                  groupItem.results.length > MAX_RESULTS_PER_GROUP && (
                    <span
                      className="text-sm"
                      style={{ color: 'var(--bq-text-secondary)' }}
                    >
                      Showing {MAX_RESULTS_PER_GROUP} of{' '}
                      {groupItem.results.length} results
                    </span>
                  )}
              </div>
              <SearchResults
                results={limitedResults}
                onResultClick={onResultClick}
                onClose={onClose}
              />
            </div>
          );
        } else return null;
      })}
    </div>
  );
};
